import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UserRole } from 'scheduler-types';
import { apiUrl, mockAuthHeaders } from '../lib/api';

export interface CreatePersonRequest {
  name: string;
  email: string;
  role: UserRole;
  skillIds?: string[];
}

export interface UpdatePersonRequest {
  name?: string;
  email?: string;
  role?: UserRole;
  isActive?: boolean;
  skillIds?: string[];
  lunchStartTime?: string | null;
  lunchDurationMinutes?: number | null;
}

async function createPerson(data: CreatePersonRequest): Promise<{ id: string }> {
  const response = await fetch(apiUrl('/api/v1/people'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: response.statusText } }));
    throw new Error(error.error?.message ?? 'Failed to create person');
  }

  const json = (await response.json()) as { data?: { id: string } };
  return json.data ?? { id: '' };
}

async function updatePerson(id: string, data: UpdatePersonRequest): Promise<{ updated: boolean }> {
  const response = await fetch(apiUrl(`/api/v1/people/${id}`), {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: response.statusText } }));
    throw new Error(error.error?.message ?? 'Failed to update person');
  }

  const json = (await response.json()) as { data?: { updated: boolean } };
  return json.data ?? { updated: false };
}

async function deletePerson(id: string): Promise<{ deleted: boolean }> {
  const response = await fetch(apiUrl(`/api/v1/people/${id}`), {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      ...mockAuthHeaders(),
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: response.statusText } }));
    throw new Error(error.error?.message ?? 'Failed to delete person');
  }

  const json = (await response.json()) as { data?: { deleted: boolean } };
  return json.data ?? { deleted: false };
}

export function useCreatePerson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createPerson,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['people'] });
    },
  });
}

export function useUpdatePerson(personId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdatePersonRequest) => updatePerson(personId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['people'] });
      queryClient.invalidateQueries({ queryKey: ['person', personId] });
    },
  });
}

export function useDeletePerson() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deletePerson,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['people'] });
    },
  });
}
